import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, CheckCircle2, ShieldCheck, Clock3, Headset } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { SERVICES, IMAGES } from "@/data/site";

const SERVICE_IMAGES = {
  "data-engineering": IMAGES.dataCenter,
  "cloud-migration": IMAGES.serverFans,
  "sas-onprem": IMAGES.devSetup,
  networking: IMAGES.rack,
  "ai-agentic": IMAGES.execDesk,
  hardware: IMAGES.laptop,
};

const Hero = () => (
  <section className="blueprint-grid" data-testid="services-hero">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-14">
      <Overline>Services</Overline>
      <h1 className="mt-4 font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl text-ink max-w-4xl">
        <MaskedLine delay={0.15}>From legacy SAS to</MaskedLine>
        <MaskedLine delay={0.3}>
          <span className="text-cobalt italic">Snowflake-grade</span> modern.
        </MaskedLine>
      </h1>
      <Reveal delay={0.45}>
        <p className="mt-6 text-base sm:text-lg text-gray-600 max-w-2xl leading-relaxed">
          Six practices, one accountable team. Every engagement is scoped, estimated and delivered by engineers who have done it at enterprise scale.
        </p>
      </Reveal>
    </div>
  </section>
);

const ServiceBlock = ({ service, index }) => {
  const flip = index % 2 === 1;
  return (
    <section className={`py-16 sm:py-20 ${index % 2 === 1 ? "bg-white border-y border-gray-200" : ""}`} data-testid={`service-section-${service.id}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid lg:grid-cols-2 gap-12 items-center`}>
          <Reveal className={flip ? "lg:order-2" : ""}>
            <div className="rounded-3xl overflow-hidden border border-gray-200 shadow-lg shadow-gray-200/60">
              <img src={SERVICE_IMAGES[service.id]} alt={service.title} className="w-full h-[340px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.12} className={flip ? "lg:order-1" : ""}>
            <p className="font-mono text-xs tracking-[0.25em] text-cobalt font-semibold">{service.num}</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-ink">
              {service.title}
            </h2>
            <p className="mt-5 text-gray-600 leading-relaxed">{service.blurb}</p>
            <ul className="mt-7 space-y-3.5">
              {service.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                  <CheckCircle2 className="h-5 w-5 text-cobalt shrink-0 mt-0.5" />
                  {p}
                </li>
              ))}
            </ul>
            <Link
              to={`/contact?service=${service.id}`}
              data-testid={`service-cta-${service.id}`}
              className="mt-8 inline-flex items-center gap-2 bg-ink text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-cobalt transition-colors duration-300"
            >
              Discuss this service
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Flow = () => {
  const steps = [
    { n: "01", t: "Assess", d: "Inventory of programs, data volumes, dependencies and SLAs." },
    { n: "02", t: "Convert", d: "Factory-model code conversion to Python / PySpark / SQL." },
    { n: "03", t: "Migrate", d: "Parallel-run on Snowflake or Databricks with reconciliation." },
    { n: "04", t: "Optimise", d: "In-database pushdown, warehouse right-sizing, cost guardrails." },
  ];
  return (
    <section className="py-20 bg-ink text-white relative grain overflow-hidden" data-testid="services-flow">
      <div className="blueprint-grid-dark absolute inset-0 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline light>How we migrate</Overline>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight max-w-2xl">
            A four-stage migration factory.
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="relative rounded-3xl bg-white/[0.06] border border-white/10 p-7 h-full" data-testid={`flow-step-${i}`}>
                <p className="font-mono text-xs tracking-[0.25em] text-cobalt">{s.n}</p>
                <h3 className="mt-3 font-display text-xl font-bold">{s.t}</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{s.d}</p>
                {i < steps.length - 1 && (
                  <ArrowDown className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 -rotate-90 h-5 w-5 text-cobalt" />
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Sla = () => (
  <section className="py-20 sm:py-24" data-testid="services-sla">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Overline>Support standards</Overline>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink max-w-2xl">
          We stay after go-live.
        </h2>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-3 gap-5">
        {[
          { icon: Clock3, t: "Response SLAs", d: "Priority response windows for production pipelines, networks and hardware under AMC." },
          { icon: ShieldCheck, t: "Governance & security", d: "PII hashing, tokenization, audit automation and access controls built into every delivery." },
          { icon: Headset, t: "Managed support", d: "Batch monitoring, incident triage and on-site hardware support from one service desk." },
        ].map((c, i) => (
          <Reveal key={c.t} delay={i * 0.1}>
            <div className="rounded-3xl bg-white border border-gray-200 p-7 h-full transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-gray-200" data-testid={`sla-card-${i}`}>
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-cobalt-light">
                <c.icon className="h-5 w-5 text-cobalt" />
              </div>
              <h3 className="mt-5 font-display text-lg font-bold text-ink">{c.t}</h3>
              <p className="mt-2.5 text-sm text-gray-600 leading-relaxed">{c.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Services() {
  return (
    <div data-testid="services-page">
      <Hero />
      <Marquee />
      {SERVICES.map((s, i) => (
        <ServiceBlock key={s.id} service={s} index={i} />
      ))}
      <Flow />
      <Sla />
    </div>
  );
}
